import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../app/hooks";
import { Loading } from "../../../../components/Loading";
import { useAdminReportsFilters } from "../useAdminReportsFilters";
import { getReportLevel, buildReportQueryString, reportSubLevelName } from "../adminReportsUtil";
import { ReportLevel, AdminReportsResponse } from "../adminReportsApiSlice";
import { DistrictSchoolFilters } from "../DistrictSchoolFilters";
import { TableRightSideHeader } from "../TableRightSideHeader";
import { EssaysAndDraftsRow, useLazyEssaysAndDraftsQuery } from "./adminUsageReportApiSlice";
import { EssaysAndDraftsTable } from "./EssaysAndDraftsTable";
import { EssaysAndDraftsChart } from "./EssaysAndDraftsChart";

export default function EssaysAndDrafts() {
	const { aspUrl } = useAppSelector((state) => state.site);
	const {
		maxReportLevel,
		districtOptions,
		schoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	} = useAdminReportsFilters();

	const navigate = useNavigate();

	const [data, setData] = useState<AdminReportsResponse<EssaysAndDraftsRow>>();
	const [lastUpdated, setLastUpdated] = useState<Date>();
	const [hideRows, setHideRows] = useState(false);
	const [reportLevel, setReportLevel] = useState<ReportLevel>(maxReportLevel);

	const [callEssaysAndDrafts] = useLazyEssaysAndDraftsQuery();

	useEffect(() => {
		// skip if don't have the necessary ids for the max level to prevent auto-fail calls
		if (maxReportLevel === ReportLevel.School && (!districtFilter || !schoolFilter)) {
			return;
		}
		if (maxReportLevel === ReportLevel.District && !districtFilter) {
			return;
		}

		callEssaysAndDrafts(
			{
				districtId: districtFilter === 0 ? undefined : districtFilter,
				schoolId: schoolFilter === 0 ? undefined : schoolFilter,
			},
			true //cache the results
		)
			.unwrap()
			.then((payload) => {
				setData(payload.response);
				setLastUpdated(payload.lastUpdateDate);
				setReportLevel(getReportLevel(districtFilter, schoolFilter));
			})
			.catch((error) => {
				console.log(error.data);
				navigate("/adminReports");
			});
	}, [districtFilter, schoolFilter]);

	const queryString = useMemo(() => {
		return buildReportQueryString({
			districtFilter,
			schoolFilter,
		});
	}, [districtFilter, schoolFilter]);

	return (
		<section>
			<h1>
				{reportLevel} Essays And Drafts Summary by {reportSubLevelName(reportLevel)}
			</h1>
			<p className="mt-4 print:hidden">
				This report shows the number of essay and draft submissions and the average number
				of drafts per essay for each {reportSubLevelName(reportLevel).toLowerCase()}.
				{schoolOptions.length > 1 || districtOptions.length > 1
					? ` Use the filters below ${reportLevel !== ReportLevel.School ? "or select the name in the table" : ""} to view data for that selection. `
					: " "}
				Click the three dots to print or download.
			</p>
			<div className="mt-3">
				<Loading loading={!data} />
				{data && (
					<>
						<div className="flex justify-between items-end">
							<DistrictSchoolFilters
								districtOptions={districtOptions}
								schoolOptions={schoolOptions}
								districtFilter={districtFilter}
								schoolFilter={schoolFilter}
								setDistrictFilter={setDistrictFilter}
								setSchoolFilter={setSchoolFilter}
							/>
							<TableRightSideHeader
								lastUpdated={lastUpdated}
								hasData={data.rows.length !== 0}
								hideRows={hideRows}
								setHideRows={setHideRows}
								uniqueKey="essays-and-drafts"
								downloadExcelLink={`${aspUrl}/AdminReports/EssaysAndDraftsExcel?${queryString}`}
								downloadCsvLink={`${aspUrl}/AdminReports/EssaysAndDraftsCsv?${queryString}`}
							/>
						</div>

						<EssaysAndDraftsTable
							className="mt-4"
							level={reportLevel}
							data={data}
							hideRows={hideRows}
							setDistrictFilter={setDistrictFilter}
							setSchoolFilter={setSchoolFilter}
						/>
						<div className="print:hidden">
							<EssaysAndDraftsChart
								level={reportLevel}
								data={data.rows}
								hasData={data.rows.length > 0}
							/>
						</div>
					</>
				)}
			</div>
		</section>
	);
}
